<template>
  <div class="ins-table-wrap">
    <!-- Toolbar: filter toggle · column chooser · result count · CSV export -->
    <div class="ins-tbl-bar" v-if="filterable || exportName || columnChooser">
      <button v-if="filterable" type="button" class="ins-tbl-btn" :class="{ on: showFilters }" @click="showFilters = !showFilters">
        ⚲ Filters<span v-if="activeFilterCount"> ({{ activeFilterCount }})</span>
      </button>
      <button v-if="filterable && activeFilterCount" type="button" class="ins-tbl-btn" @click="clearFilters">Clear</button>

      <div v-if="columnChooser" class="ins-tbl-cols">
        <button type="button" class="ins-tbl-btn" @click="showCols = !showCols">▦ Columns</button>
        <div v-if="showCols" class="ins-tbl-colmenu" @click.stop>
          <label v-for="c in columns" :key="'cc' + c.key" class="ins-tbl-colopt">
            <input type="checkbox" :checked="!hidden[c.key]" @change="toggleCol(c.key)" /> {{ c.label }}
          </label>
        </div>
      </div>

      <span class="ins-tbl-count">{{ sortedRows.length }}<template v-if="sortedRows.length !== rows.length"> of {{ rows.length }}</template> {{ rowNoun }}</span>
      <span class="ins-tbl-spacer"></span>
      <button v-if="exportName" type="button" class="ins-tbl-btn" :disabled="!sortedRows.length" @click="exportCsv">⬇ CSV</button>
    </div>

    <div class="ins-tbl-scroll">
      <table class="ins-inv-tbl ins-table" :class="{ dense }">
        <thead>
          <tr>
            <th v-for="c in visibleColumns" :key="c.key" :class="[c.cls, c.num ? 'num' : '', { sortable: c.sortable !== false, sorted: iSortKey === c.key }]"
                :style="c.width ? { width: c.width } : null"
                @click="c.sortable !== false && toggleSort(c)">
              <span class="ins-th-label">{{ c.label }}</span><InsInfoTip v-if="c.termKey || c.tip" :term-key="c.termKey || ''" :text="c.tip || ''" :glossary="glossary" :mode="tipMode" />
              <span v-if="c.sortable !== false" class="ins-th-arrow">{{ iSortKey === c.key ? (iSortDir === 1 ? "▲" : "▼") : "" }}</span>
            </th>
          </tr>
          <!-- per-column filter row -->
          <tr v-if="showFilters" class="ins-tbl-filters">
            <th v-for="c in visibleColumns" :key="'f' + c.key" :class="c.num ? 'num' : ''">
              <template v-if="c.filter === 'select'">
                <select :value="filters[c.key] || ''" @change="setFilter(c.key, $event.target.value)" @click.stop>
                  <option value="">All</option>
                  <option v-for="opt in selectOptions(c)" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </template>
              <template v-else-if="c.filter === 'number'">
                <div class="ins-tbl-range" @click.stop>
                  <input type="number" class="ins-tbl-num" placeholder="min" :value="(filters[c.key] || {}).min"
                         @input="setRange(c.key, 'min', $event.target.value)" />
                  <input type="number" class="ins-tbl-num" placeholder="max" :value="(filters[c.key] || {}).max"
                         @input="setRange(c.key, 'max', $event.target.value)" />
                </div>
              </template>
              <template v-else-if="c.filter === 'text'">
                <input type="text" class="ins-tbl-txt" placeholder="filter…" :value="filters[c.key] || ''"
                       @input="setFilter(c.key, $event.target.value)" @click.stop />
              </template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in pagedRows" :key="rowKey ? row[rowKey] : ri" :class="rowClass ? rowClass(row) : ''"
              @click="$emit('row-click', row)">
            <td v-for="c in visibleColumns" :key="c.key" :class="[c.cls, c.num ? 'num' : '']">
              <slot :name="'cell-' + c.key" :row="row" :value="rawValue(row, c)">{{ display(row, c) }}</slot>
            </td>
          </tr>
          <tr v-if="!pagedRows.length">
            <td :colspan="visibleColumns.length" class="ins-tbl-empty">{{ emptyText }}</td>
          </tr>
        </tbody>
        <tfoot v-if="$slots.foot"><slot name="foot" :rows="sortedRows" /></tfoot>
      </table>
    </div>

    <!-- pagination: page-size selector + prev/next + the exact range on screen.
         Shown as soon as there are more rows than the smallest option, so the selector is
         always reachable even when the current size already fits everything. -->
    <div v-if="showPager" class="ins-tbl-pager">
      <label class="ins-tbl-psz">
        Show
        <select v-model.number="size" @change="page = 0" aria-label="Rows per page">
          <option v-for="n in sizeOptions" :key="n" :value="n">{{ n === 0 ? 'All' : n }}</option>
        </select>
        rows
      </label>
      <span class="ins-tbl-pgsp"></span>
      <template v-if="size">
        <button type="button" class="ins-tbl-btn" :disabled="page === 0" @click="page = 0" title="First page">«</button>
        <button type="button" class="ins-tbl-btn" :disabled="page === 0" @click="page--">‹ Prev</button>
        <span>{{ rangeLabel }}</span>
        <button type="button" class="ins-tbl-btn" :disabled="page >= pageCount - 1" @click="page++">Next ›</button>
        <button type="button" class="ins-tbl-btn" :disabled="page >= pageCount - 1" @click="page = pageCount - 1" title="Last page">»</button>
      </template>
      <span v-else>{{ rangeLabel }}</span>
    </div>
  </div>
</template>

<script>
/* Shared sortable / filterable / exportable table for the Insights reports (WOI / FI / MI).
 * Follows the design-system golden rule — the table FRAMES, the report OWNS its data and (optionally)
 * its cell markup via `#cell-<key>` slots. No data fetching, no schema knowledge.
 *
 * columns: [{
 *   key, label,
 *   sortable = true, num = false,          // num → right-align + numeric sort
 *   filter = 'none'|'text'|'number'|'select',
 *   termKey, tip,                          // header info bubble (via InsInfoTip + glossary, or literal tip text)
 *   fmt(value, row),                       // optional display formatter (raw value is used for sort/filter/export)
 *   cls, width,
 * }]
 * Emits `row-click`. Cell override: <template #cell-part="{ row, value }"> … </template>.
 */
import InsInfoTip from "./InsInfoTip.vue";
import { downloadCsv } from "./insightsExport";

export default {
  name: "InsTable",
  components: { InsInfoTip },
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    glossary: { type: Object, default: () => ({}) },
    sortKey: { type: String, default: "" },
    sortDir: { type: Number, default: -1 },      // 1 asc, -1 desc
    filterable: { type: Boolean, default: true },
    columnChooser: { type: Boolean, default: false },
    tipMode: { type: String, default: "hover" }, // 'hover' | 'click'
    /* Initial rows per page. 0 = show all. A report's explicit value is honoured as the
       STARTING choice; the reader can change it with the selector below. Default 10 so a
       long table never dumps hundreds of rows at once. */
    pageSize: { type: Number, default: 10 },
    /* Offered sizes. 0 means "All" and is appended automatically, so a reader is never
       trapped in pages when they want the whole set (that used to be the behaviour for
       tables that passed no pageSize at all). */
    pageSizes: { type: Array, default: () => [10, 20, 50, 100] },
    exportName: { type: String, default: "" },    // filename base → enables CSV export
    rowKey: { type: String, default: "" },
    rowClass: { type: Function, default: null },
    rowNoun: { type: String, default: "rows" },
    emptyText: { type: String, default: "No rows match these filters." },
    dense: { type: Boolean, default: false },
  },
  data() {
    return {
      iSortKey: this.sortKey,
      iSortDir: this.sortDir === 1 ? 1 : -1,
      filters: {},        // key -> string | string[] | {min,max}
      hidden: {},         // key -> true when column hidden
      showFilters: false,
      showCols: false,
      page: 0,
      size: this.pageSize,   // live selection; starts at the report's pageSize
    };
  },
  watch: {
    // If the report changes pageSize reactively, follow it and go back to page 1.
    pageSize(v) { this.size = v; this.page = 0; },
    // Filtering/sorting can shrink the set under the current page — never strand the
    // reader on an empty page they cannot see their way off.
    sortedRows() { if (this.page > this.pageCount - 1) this.page = Math.max(0, this.pageCount - 1); },
  },
  computed: {
    visibleColumns() { return this.columns.filter((c) => !this.hidden[c.key]); },
    activeFilterCount() {
      return Object.keys(this.filters).filter((k) => {
        const f = this.filters[k];
        if (f == null || f === "") return false;
        if (typeof f === "object" && !Array.isArray(f)) return f.min != null && f.min !== "" || f.max != null && f.max !== "";
        return true;
      }).length;
    },
    filteredRows() {
      const active = this.activeFilterCount;
      if (!active) return this.rows;
      return this.rows.filter((row) => this.matches(row));
    },
    sortedRows() {
      const k = this.iSortKey;
      if (!k) return this.filteredRows;
      const col = this.columns.find((c) => c.key === k) || {};
      const dir = this.iSortDir;
      const num = !!col.num;
      return this.filteredRows.slice().sort((a, b) => {
        let av = a[k], bv = b[k];
        if (num) { av = this.toNum(av); bv = this.toNum(bv); return (av - bv) * dir; }
        av = av == null ? "" : String(av).toLowerCase();
        bv = bv == null ? "" : String(bv).toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    },
    /* Options = the standard sizes, plus the report's own pageSize when it is not one of
       them (e.g. a drill that asked for 24), plus All. Sorted, deduped, All last. */
    sizeOptions() {
      const base = (Array.isArray(this.pageSizes) ? this.pageSizes : [10, 20, 50, 100])
        .map(Number).filter(n => n > 0);
      if (this.pageSize > 0 && base.indexOf(this.pageSize) === -1) base.push(this.pageSize);
      return base.sort((a, b) => a - b).filter((n, i, a) => a.indexOf(n) === i).concat([0]);
    },
    // Reachable as soon as the set exceeds the smallest offered size, so the reader can
    // always change it — not only once the CURRENT size overflows.
    showPager() {
      const smallest = this.sizeOptions.filter(n => n > 0)[0] || 10;
      return this.sortedRows.length > smallest;
    },
    rangeLabel() {
      const t = this.sortedRows.length;
      if (!this.size) return `all ${t.toLocaleString()} ${this.rowNoun}`;
      const a = t === 0 ? 0 : this.page * this.size + 1;
      const b = Math.min(t, (this.page + 1) * this.size);
      return `${a.toLocaleString()}–${b.toLocaleString()} of ${t.toLocaleString()}`;
    },
    pageCount() { return this.size ? Math.max(1, Math.ceil(this.sortedRows.length / this.size)) : 1; },
    pagedRows() {
      // `size` (the live selection), not `pageSize` (the initial prop).
      if (!this.size) return this.sortedRows;
      const start = this.page * this.size;
      return this.sortedRows.slice(start, start + this.size);
    },
  },
  watch: {
    sortedRows() { if (this.page >= this.pageCount) this.page = 0; },
  },
  methods: {
    toNum(v) { const n = typeof v === "number" ? v : parseFloat(String(v == null ? "" : v).replace(/[^0-9.-]/g, "")); return isNaN(n) ? -Infinity : n; },
    toggleSort(col) {
      if (this.iSortKey === col.key) this.iSortDir = -this.iSortDir;
      else { this.iSortKey = col.key; this.iSortDir = col.num ? -1 : 1; }
    },
    rawValue(row, col) { return row[col.key]; },
    display(row, col) {
      const v = row[col.key];
      return col.fmt ? col.fmt(v, row) : (v == null ? "" : v);
    },
    setFilter(key, val) { this.$set(this.filters, key, val); this.page = 0; },
    setRange(key, which, val) {
      const cur = Object.assign({}, this.filters[key] || {});
      cur[which] = val;
      this.$set(this.filters, key, cur);
      this.page = 0;
    },
    clearFilters() { this.filters = {}; this.page = 0; },
    toggleCol(key) { this.$set(this.hidden, key, !this.hidden[key]); },
    selectOptions(col) {
      const seen = new Set();
      this.rows.forEach((r) => { const v = r[col.key]; if (v != null && v !== "") seen.add(String(v)); });
      return Array.from(seen).sort((a, b) => a.localeCompare(b));
    },
    matches(row) {
      for (const key in this.filters) {
        const f = this.filters[key];
        if (f == null || f === "") continue;
        const raw = row[key];
        if (typeof f === "object" && !Array.isArray(f)) {
          const n = this.toNum(raw);
          if (f.min != null && f.min !== "" && n < parseFloat(f.min)) return false;
          if (f.max != null && f.max !== "" && n > parseFloat(f.max)) return false;
        } else if (Array.isArray(f)) {
          if (f.length && !f.includes(String(raw))) return false;
        } else {
          // select (exact) uses the same string box; text uses contains — both handled as contains,
          // which also satisfies an exact select match.
          if (!String(raw == null ? "" : raw).toLowerCase().includes(String(f).toLowerCase())) return false;
        }
      }
      return true;
    },
    exportCsv() {
      const cols = this.visibleColumns;
      const headers = cols.map((c) => c.label);
      const rows = this.sortedRows.map((r) => cols.map((c) => {
        const v = r[c.key];
        return v == null ? "" : v;
      }));
      const name = /\.csv$/i.test(this.exportName) ? this.exportName : this.exportName + ".csv";
      downloadCsv(name, headers, rows);
    },
  },
};
</script>

<style scoped>
.ins-table-wrap { width: 100%; }
.ins-tbl-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.ins-tbl-spacer { flex: 1 1 auto; }
.ins-tbl-count { font-size: 12px; color: var(--ins-label, #7a828b); }
.ins-tbl-btn {
  font-size: 12px; font-weight: 600; color: var(--ins-text, #46525e);
  background: var(--ins-card, #fff); border: 1px solid var(--ins-border, #dfe5ea);
  border-radius: 6px; padding: 4px 9px; cursor: pointer; line-height: 1.2;
  transition: border-color .12s ease, color .12s ease, background .12s ease;
}
.ins-tbl-btn:hover { border-color: var(--ins-accent, #2f6db0); color: var(--ins-accent, #2f6db0); }
.ins-tbl-btn.on { background: var(--ins-accent, #2f6db0); color: #fff; border-color: var(--ins-accent, #2f6db0); }
.ins-tbl-btn:disabled { opacity: .5; cursor: default; }
.ins-tbl-cols { position: relative; }
.ins-tbl-colmenu {
  position: absolute; z-index: 40; top: 110%; left: 0; min-width: 170px;
  background: var(--ins-card, #fff); border: 1px solid var(--ins-border, #dfe5ea);
  border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,.14); padding: 6px; max-height: 260px; overflow: auto;
}
.ins-tbl-colopt { display: flex; align-items: center; gap: 7px; font-size: 12px; padding: 4px 6px; color: var(--ins-text, #46525e); cursor: pointer; }
.ins-tbl-colopt:hover { background: var(--ins-track, #f1f5f9); border-radius: 5px; }
.ins-tbl-scroll { overflow-x: auto; }
.ins-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ins-table.dense { font-size: 12px; }
/* Self-contained cell styling so InsTable looks right even outside a themed `.insights-report` wrapper. */
.ins-table th, .ins-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--ins-border, #eef2f5); color: var(--ins-text, #46525e); vertical-align: top; }
.ins-table thead th { color: var(--ins-label, #7a828b); font-weight: 700; font-size: 11px; text-transform: uppercase; }
.ins-table th.num, .ins-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
.ins-table.dense th, .ins-table.dense td { padding: 4px 6px; }
.ins-table tfoot td { font-weight: 700; color: var(--ins-text, #2a2a2a); border-top: 2px solid var(--ins-border, #e4e9ee); }
/* subtle row hover — signals interactivity + eases scanning across wide tables */
.ins-table tbody tr { transition: background .1s ease; }
.ins-table tbody tr:hover td { background: var(--ins-track, #f1f5f9); }
.ins-table th.sortable { cursor: pointer; user-select: none; white-space: nowrap; transition: color .12s ease; }
.ins-table th.sortable:hover { color: var(--ins-accent, #2f6db0); }
.ins-table th.sorted { color: var(--ins-accent, #2f6db0); }
.ins-th-arrow { display: inline-block; width: 10px; margin-left: 2px; font-size: 9px; color: var(--ins-accent, #2f6db0); }
.ins-tbl-filters th { padding-top: 4px; padding-bottom: 6px; background: var(--ins-track, #f7f9fb); }
.ins-tbl-filters input, .ins-tbl-filters select {
  width: 100%; box-sizing: border-box; font-size: 11px; padding: 3px 6px;
  border: 1px solid var(--ins-border, #dfe5ea); border-radius: 5px; background: var(--ins-card, #fff); color: var(--ins-text, #46525e);
  transition: border-color .12s ease, box-shadow .12s ease;
}
.ins-tbl-filters input:focus, .ins-tbl-filters select:focus {
  border-color: var(--ins-accent, #2f6db0); outline: none;
  box-shadow: 0 0 0 2px rgba(47,109,176,.14);
}
.ins-tbl-range { display: flex; gap: 3px; }
.ins-tbl-num { min-width: 0; }
.ins-tbl-empty { text-align: center; color: var(--ins-label, #97a0a9); padding: 14px 8px; font-style: italic; }
.ins-tbl-pager { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 8px; font-size: 12px; color: var(--ins-label, #7a828b); flex-wrap: wrap; }
.ins-tbl-psz { display: inline-flex; align-items: center; gap: 5px; }
.ins-tbl-psz select { padding: 2px 6px; border: 1px solid var(--ins-border, #d9dfe5); border-radius: 5px; font-size: 12px; font-family: inherit; background: var(--ins-card, #fff); color: inherit; cursor: pointer; }
.ins-tbl-pgsp { width: 6px; }
html.dark-mode .ins-tbl-psz select { background: #1b222b; border-color: #353d47; color: #c7ced6; }
/* Dark mode — set explicitly (not via wrapper vars) so InsTable reads correctly in EVERY report,
   including FI (.fi-wrap) and MI (.mi-wrap) which aren't wrapped in .insights-report. */
html.dark-mode .ins-table th, html.dark-mode .ins-table td { color: #c7ced6; border-bottom-color: #2a3139; }
html.dark-mode .ins-table thead th { color: #8b95a1; }
html.dark-mode .ins-table tfoot td { color: #e7ebef; border-top-color: #353d47; }
html.dark-mode .ins-table tbody tr:hover td { background: rgba(255,255,255,.055); }
html.dark-mode .ins-tbl-count, html.dark-mode .ins-tbl-pager { color: #8b95a1; }
html.dark-mode .ins-tbl-btn { background: #1b222b; border-color: #353d47; color: #c7ced6; }
html.dark-mode .ins-tbl-btn.on { background: var(--ins-accent, #2f6db0); border-color: var(--ins-accent, #2f6db0); color: #fff; }
html.dark-mode .ins-tbl-filters th { background: rgba(255,255,255,.03); }
html.dark-mode .ins-tbl-filters input, html.dark-mode .ins-tbl-filters select { background: #141a21; border-color: #353d47; color: #c7ced6; }
html.dark-mode .ins-tbl-colmenu { background: #1b222b; border-color: #353d47; box-shadow: 0 6px 20px rgba(0,0,0,.5); }
html.dark-mode .ins-tbl-colopt { color: #c7ced6; }
html.dark-mode .ins-tbl-colopt:hover { background: rgba(255,255,255,.06); }
@media print { .ins-tbl-bar, .ins-tbl-pager, .ins-th-arrow { display: none !important; } }
</style>
